import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Hourglass, Clock } from "lucide-react";

import { useToast } from "../../hooks/use-toast";
import ConfirmActionModal from "./ConfirmActionModal";
import { OPEN_MISSED_CHECKOUT_EVENT } from "../../utils/missedCheckout";
import {
  useReviewAttendanceMutation,
  useGetRecordsByRoleQuery,
} from "../../redux/hrms/attendanceAPI";
import { formatDate } from "../../utils/dateFormat";

const getCurrentLocation = () =>
  new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Geolocation is not supported in this browser"));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
      },
      (error) => reject(error),
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  });

export default function MissedCheckoutPopup() {
  const user = useSelector((state) => state.auth?.user || null);
  const { toast } = useToast();
  const [submitMissedCheckout, { isLoading: isSubmittingMissed }] = useReviewAttendanceMutation();

  const validUserID = user?.smaId && user.smaId !== "undefined";

  const {
    data: attendanceResponse,
    refetch,
  } = useGetRecordsByRoleQuery(
    {
      page: 0,
      size: 10,
      sortBy: "checkInTime",
      sortDir: "desc",
      filter: {
        employeeName: "",
        fromDate: "",
        toDate: "",
        status: "",
        scope: "SELF",
      },
    },
    { skip: !validUserID }
  );

  const missedRecord = useMemo(() => {
    const records = attendanceResponse?.content || [];
    const today = new Date().toISOString().split("T")[0];
    return records.find((rec) => !rec.checkOutTime && rec.date < today) || null;
  }, [attendanceResponse]);

  const [dismissedId, setDismissedId] = useState(null);
  const [comment, setComment] = useState("");
  const [selectedTime, setSelectedTime] = useState("");

  const hasMissedCheckout = Boolean(missedRecord);
  const isOpen = Boolean(missedRecord) && missedRecord.id !== dismissedId;
  const openModal = () => setDismissedId(null);
  const closeModal = () => setDismissedId(missedRecord?.id ?? null);

  useEffect(() => {
    const handler = () => setDismissedId(null);
    window.addEventListener(OPEN_MISSED_CHECKOUT_EVENT, handler);
    return () => window.removeEventListener(OPEN_MISSED_CHECKOUT_EVENT, handler);
  }, []);

  const handleSubmitMissedCheckout = async () => {
    if (!missedRecord) return;

    // Instant, exact feedback before any network call (the server enforces
    // the same rule and returns the same wording).
    const checkIn = missedRecord.checkInTime
      ? new Date(missedRecord.checkInTime)
      : null;
    const checkOut = new Date(`${missedRecord.date}T${selectedTime}:00`);
    if (
      checkIn &&
      !Number.isNaN(checkIn.getTime()) &&
      !Number.isNaN(checkOut.getTime()) &&
      checkOut < checkIn
    ) {
      const fmtTime = (d) =>
        d.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
      toast({
        variant: "destructive",
        title: "Invalid time range",
        description: `Check-out time (${fmtTime(checkOut)}) must be greater than check-in time (${fmtTime(checkIn)})`,
      });
      return;
    }

    try {
      const coords = await getCurrentLocation();
      const payload = {
        recordID: missedRecord.id,
        userID: user.smaId,
        missedCheckoutReason: comment,
        checkOutTime: `${missedRecord.date}T${selectedTime}:00`,
        checkOutLatitude: coords.latitude,
        checkOutLongitude: coords.longitude,
        checkOutLocationName: "",
      };
      await submitMissedCheckout(payload).unwrap();
      toast({
        variant: "success",
        title: "Checkout completed",
        description: "Your missed checkout has been recorded successfully.",
      });
      setDismissedId(null);
      setComment("");
      setSelectedTime("");
      refetch();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Checkout failed",
        description:
          error?.data?.message ||
          error?.message ||
          "Unable to complete checkout.",
      });
    }
  };

  // Same DD/Mon/YYYY style used across attendance views.
  const missedCheckoutDate = missedRecord?.date
    ? formatDate(missedRecord.date)
    : "";

  const missedCheckoutTitle = missedCheckoutDate
    ? `You forgot to check out on ${missedCheckoutDate}!`
    : "You forgot to check out!";

  return (
    <>
      <ConfirmActionModal
        isOpen={isOpen}
        onClose={closeModal}
        onConfirm={handleSubmitMissedCheckout}
        title={missedCheckoutTitle}
        description=""
        comment={comment}
        setComment={setComment}
        commentLabel="Reason"
        showRequiredAsterisk={true}
        commentPlaceholder="Enter reason..."
        commentRequired={true}
        confirmText="Check Out"
        confirmVariant="default"
        isLoading={isSubmittingMissed}
        showDateInput={true}
        showTimeInput={true}
        selectedTime={selectedTime}
        setSelectedTime={setSelectedTime}
        selectedDate={missedRecord?.date || ""}
        setSelectedDate={() => {}}
        dateDisabled={true}
        hideIcon={true}
        compact={true}
        hideCancelButton={true}
      />

      {hasMissedCheckout && (
        <button
          onClick={openModal}
          className="fixed bottom-6 right-6 z-50 inline-flex animate-pulse items-center gap-2 rounded-full border border-amber-500/30 bg-amber-500 text-white px-4 py-2.5 text-sm font-semibold shadow-lg shadow-amber-500/25 transition-all hover:scale-105 hover:bg-amber-600"
        >
          <Clock className="h-4 w-4" />
          Pending Check In
          <span className="flex h-4 w-4 items-center justify-center rounded-full bg-white/25 text-[10px] font-bold">
            <Hourglass className="h-3 w-3" />
          </span>
        </button>
      )}
    </>
  );
}
